import ContactSection from "../../components/ContactForm";
import Header from "../../components/Header";
import { motion } from 'framer-motion';
import { Printer, Scissors, Box, Layers, Award, Shield, Clock, ArrowRight } from 'lucide-react';
import HeroSection from "../../components/HeroSection";
import ServicesSection from "../../components/Services";
import bannerImg from "../../assets/Firefly 20250214075131.png";
import { Link, useLocation } from "react-router-dom";
import Footer from "../../components/Footer";
import { useEffect } from "react";

export default function PostPress() {

  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      icon: <Box className="w-12 h-12 text-primary-base" />,
      title: "Binding",
      description: "We offer a variety of binding options such as perfect binding with creasing, saddle stitching, section sewing, and hard case binding for different types of print projects."
    },
    {
      icon: <Scissors className="w-12 h-12 text-primary-base" />,
      title: "Cutting & Embossing",
      description: "Our precision cutting services ensure clean edges, while embossing adds a distinct touch of elegance to your printed materials."
    },
    {
      icon: <Layers className="w-12 h-12 text-primary-base" />,
      title: "Coating Services",
      description: "We provide high-quality coating services including Thermal Lamination, Aqua Coating, and UV Varnishing to enhance the look and durability of your prints."
    }
  ];

  const benefits = [
    {
      icon: <Award className="w-8 h-8" />,
      title: "High-Quality Finishing",
      description: "Our post-press services guarantee flawless finishes and exceptional quality on every project."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Tailored Solutions",
      description: "We understand that every project is unique, and we provide customized solutions to meet your specific requirements."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Quick Turnaround",
      description: "Get your finished products quickly, without compromising quality, thanks to our efficient post-press process."
    }
  ];

  return (
    <div className="font-body">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-dark to-primary-base py-20 lg:py-32">
        <div className="absolute inset-0 bg-grid-white/[0.2] bg-[size:16px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-8">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-accent-base to-accent-light">
                Post Press Finishing Services
              </span>
            </h1>
            <p className="text-xl text-neutral-200 max-w-2xl mx-auto mb-8">
              Our post-press services add the perfect finishing touches to your print materials, from binding to coating to cutting and embossing.
            </p>
            <Link to="/contact">
                <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl">
                Get Started Today
                </button>
              </Link>
            
          </motion.div>
        </div>
      </section>

      {/* What is Post Press Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-15"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              What is Post Press?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Post-press services refer to the final stage of the printing process. After your items are printed, we enhance their durability, appearance, and presentation with services like binding, cutting, embossing, and coating.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Post Press Services */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base text-4xl font-bold text-center text-primary-base mb-16">
            Our Post Press Services
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="mb-6">{service.icon}</div>
                <h3 className="text-2xl font-bold text-primary-base mb-4">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

  

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base text-4xl font-bold text-center text-primary-base mb-16">
            Why Choose Our Post Press Services?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={index}
                className="p-6 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors duration-300"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="flex items-center mb-4 text-primary-base">
                  {benefit.icon}
                  <h3 className="text-xl font-bold ml-3">{benefit.title}</h3>
                </div>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-primary-dark to-primary-base text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white text-4xl font-bold mb-8">Ready to Get Started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Enhance your print materials with our high-quality post-press finishing services. Contact us today to discuss your next project!
          </p>
          <Link to="/contact">
          <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 flex items-center mx-auto">
            Contact Us Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
          </Link>
        </div>
      </section>

      <ServicesSection />

      {/* Contact Section */}
      <ContactSection />
      <Footer />
    </div>
  );
}
